import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, jsonBody } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { Card, CardHeader, ErrorPanel, Notice, StatusPill } from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";
import type { BillingStatus, Redirect } from "./types";

// Taking a customer's money is the integration people look for first, so it is the first panel
// they find. The authority to change it stays where it already was: connecting a payment account
// commits the organisation to a financial relationship, so it remains the owner's decision even
// though the rest of this area is open to administrators.
export function PaymentsPanel() {
  const auth = useAuth();
  const canManage = membershipAllows(auth.session?.selectedMembership, "tenant.manage");
  const sensitive = useSensitiveAction({
    setupTitle: "Protect payment-account changes with two-step verification",
    lockedTitle: "Confirm your identity to manage the payment account",
    lockedDescription:
      "These changes decide where your customers' payments are settled. Enter a fresh code before continuing.",
  });
  const billing = useQuery({
    queryKey: ["billing", "status"],
    queryFn: () => api<BillingStatus>("/api/v1/billing/status"),
    enabled: canManage,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function redirect(path: string, body?: unknown) {
    setWorking(true);
    setError(undefined);
    try {
      const result = await api<Redirect>(path, { method: "POST", body: jsonBody(body ?? {}) });
      window.location.assign(result.url);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
      setWorking(false);
    }
  }

  async function syncConnect() {
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/billing/connect/sync", { method: "POST", body: jsonBody({}) });
      await billing.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (canManage && billing.isLoading) return <PanelSkeleton table={false} />;
  const account = billing.data?.connectedAccount;
  return (
    <div className="stack">
      <Notice tone="info" title="Two money scopes, kept apart">
        This account receives the payments your customers make to you. The subscription you pay for
        this platform is separate and lives under Settings.
      </Notice>
      {sensitive.view}
      {error || billing.error ? <ErrorPanel error={error ?? billing.error} /> : null}
      <Card>
        <CardHeader
          title="Stripe Connect"
          description="Stripe Connect routes your customers' payments to your own connected account."
          actions={account ? <StatusPill value={account.state} /> : null}
        />
        {canManage && account ? (
          <div className="stack">
            <dl className="definition-list">
              <dt>Country</dt>
              <dd>{account.country || "Not selected"}</dd>
              <dt>Charges</dt>
              <dd>{account.chargesEnabled ? "Enabled" : "Not enabled"}</dd>
              <dt>Payouts</dt>
              <dd>{account.payoutsEnabled ? "Enabled" : "Not enabled"}</dd>
              <dt>Details submitted</dt>
              <dd>{account.detailsSubmitted ? "Yes" : "No"}</dd>
            </dl>
            {account.disabledReason ? (
              <Notice tone="danger">{account.disabledReason}</Notice>
            ) : null}
            <div className="inline">
              <button
                className="button primary"
                type="button"
                disabled={working || !sensitive.unlocked}
                onClick={() =>
                  redirect("/api/v1/billing/connect/onboarding", {
                    country: account.country || "US",
                  })
                }
              >
                {account.state === "not_started" ? "Begin onboarding" : "Continue onboarding"}
              </button>
              <button
                className="button secondary"
                type="button"
                disabled={working || !sensitive.unlocked}
                onClick={syncConnect}
              >
                Refresh account status
              </button>
            </div>
          </div>
        ) : (
          <Notice tone="warning">
            Only the workspace owner can view or manage the connected payment account.
          </Notice>
        )}
      </Card>
    </div>
  );
}
